"use client";

import styles from "@/styles/product-detail/swiper.module.scss";
import { Swiper } from "react-vant";
import Image from "next/image";
import type { PictureItem } from "@/types/product";
import { useEffect, useRef, useState } from "react";

interface Props {
  pictures: PictureItem[] | [];
}
export default function ProductSwiper({ pictures }: Props) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [revealAll, setRevealAll] = useState(false);
  const hasIgnoredInitChange = useRef(false);
  const [loadedIndexes, setLoadedIndexes] = useState<Set<number>>(
    () => new Set([0, 1]),
  );

  const handleChange = (index: number) => {
    // react-vant Swiper may fire an initial change event (index 0) on mount.
    if (!hasIgnoredInitChange.current && index === 0) {
      hasIgnoredInitChange.current = true;
      setCurrentIndex(index);
      return;
    }

    hasIgnoredInitChange.current = true;
    setCurrentIndex(index);
    setRevealAll(true);
    setLoadedIndexes((prev) => {
      if (prev.has(index)) {
        return prev;
      }
      const next = new Set(prev);
      next.add(index);
      return next;
    });
  };

  useEffect(() => {
    const firstMagnifierImage = document.getElementById("firstMagnifierImage");
    if (firstMagnifierImage) {
      firstMagnifierImage.remove();
    }
  }, []);

  useEffect(() => {
    setCurrentIndex(0);
    setRevealAll(false);
    setLoadedIndexes(new Set([0, 1]));
    hasIgnoredInitChange.current = false;
  }, [pictures]);

  useEffect(() => {
    if (revealAll) return;

    const timer = setTimeout(() => {
      setRevealAll(true);
    }, 8000);

    return () => clearTimeout(timer);
  }, [revealAll, pictures]);

  return (
    <div className={styles["comp-product-swiper"]}>
      <Swiper
        onChange={handleChange}
        indicator={() => <></>}
      >
        {pictures.map((image, index: number) => (
          <Swiper.Item key={image.fileNameUrl + index}>
            {/* 首屏仅渲染前两张，首次滑动或8秒后切换为全量渲染 */}
            {revealAll || loadedIndexes.has(index) ? (
              <Image
                src={image.imageHDUrl || image.imageORUrl || image.fileNameUrl}
                width={780}
                height={780}
                className={styles["swiper-image"]}
                alt={image.fileNameUrl}
                priority={index === 0}
                loading={index < 2 || revealAll ? "eager" : "lazy"}
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 70vw, 600px"
              />
            ) : (
              <div className={styles["swiper-image"]} />
            )}
          </Swiper.Item>
        ))}
      </Swiper>

      <div className={styles["progress-bar-container"]}>
        <div
          className={styles["progress-bar"]}
          style={{
            width: `${((currentIndex + 1) / pictures.length) * 100}%`,
          }}
        ></div>
      </div>
    </div>
  );
}
